import { Component, ErrorInfo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { captureExceptionSafe } from "@/lib/sentry";
import { markErrorReported } from "@/lib/errorReporting";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches React runtime errors,
 * logs them to admin_alerts via the log_frontend_error RPC,
 * and shows a recovery UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Mark this error so window.onerror does not double-report it
    markErrorReported(error);
    // Report to Sentry (PII-safe) — tag mechanism for filtering
    captureExceptionSafe(error, {
      mechanism: "react.errorboundary",
      componentStack: info.componentStack || "",
      url: window.location.pathname,
    });
    // Also report via existing RPC
    this.reportError(error, info.componentStack || "");
  }

  private async reportError(error: Error, componentStack: string) {
    try {
      await supabase.rpc("log_frontend_error", {
        p_error_message: `${error.name}: ${error.message}`,
        p_stack_trace: `${error.stack || ""}\n\nComponent Stack:${componentStack}`.slice(0, 2000),
        p_url: window.location.href,
        p_user_agent: navigator.userAgent,
      });
    } catch {
      // Best-effort — don't crash the error boundary
      console.error("Failed to report error to monitoring:", error);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">
              We've been notified and are looking into it. Please try refreshing the page.
            </p>
            {this.state.error && (
              <p className="text-xs text-muted-foreground font-mono bg-muted p-3 rounded-lg text-left overflow-auto max-h-32">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={this.handleReload}
              className="inline-flex items-center px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
